import { Alert, Button, Link, Spinner } from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { useMutation } from '@apollo/client';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { CREATE_CONTAINERS } from '../../../services/container-management/queries';
import {
  CreateContainersRequest,
  CreateContainersResponse,
  CreatedContainer,
} from '../../../services/container-management/types';

export type CreateContainerAlertsProps = {
  createContainersRequest: CreateContainersRequest | null;
  setCreateContainersResponse: (response: CreateContainersResponse) => void;
  createContainersResponse: CreateContainersResponse | null;
  setHasSubmitted: (hasSubmitted: boolean) => void;
  hasSubmitted: boolean;
};

const CreateContainerAlerts = (props: CreateContainerAlertsProps): ReactElement => {
  const {
    createContainersRequest,
    setCreateContainersResponse,
    createContainersResponse,
    setHasSubmitted,
    hasSubmitted,
  } = props;
  const { jsonToCSV } = usePapaParse();
  const { t } = useTranslation();

  const [visibleSuccess, setVisibleSuccess] = useState(true);
  const [visibleFailure, setVisibleFailure] = useState(true);
  const [createContainerError, setCreateContainerError] = useState<string>('');
  const [csvData, setCsvData] = React.useState<any[]>([]);
  const [csvFileName, setCsvFileName] = React.useState('');

  const updateCsv = (containers: CreatedContainer[]): void => {
    const now = new Date();
    const newCsvFileName =
      `CreatedContainers${now.getFullYear()}-${now.getMonth()}-${now.getDate()}-` +
      `${now.getHours().toString()}-${now.getMinutes().toString()}-${now.getSeconds().toString()}`;

    const rows: any[] = [];
    containers.forEach((container: CreatedContainer) => {
      if (!container.error && container.containerInstance) {
        const newCsvRow = {
          'Container ID': container.containerInstance.containerId,
          'Container Name': container.containerInstance.containerName,
          Active: 'true',
        };
        rows.push(newCsvRow);
      }
    });

    setCsvData(rows);
    setCsvFileName(newCsvFileName);
  };

  const [createContainers, { loading: creatingContainers }] = useMutation(CREATE_CONTAINERS, {
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const createContainerResponse: CreateContainersResponse = result.createContainers;
      setCreateContainersResponse(result.createContainers);
      updateCsv(createContainerResponse.createdContainers);
    },
    onError: (error) => {
      setCreateContainerError(error.message);
      setHasSubmitted(false);
    },
  });

  // Calls createContainer API for the list of bins
  useEffect(() => {
    if (hasSubmitted && createContainersRequest) {
      setVisibleSuccess(true);
      setVisibleFailure(true);
      createContainers({
        variables: {
          containers: createContainersRequest.containers,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted]);

  const downloadCsv = () => {
    const blob = new Blob([`${jsonToCSV(csvData)}`], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `${csvFileName}.csv`);
  };

  // Creates success Alerts
  const renderSuccessAlerts = () => {
    const createdContainers = createContainersResponse?.createdContainers?.filter(
      (c) => !c.error && c.containerInstance?.containerId
    );
    if (createdContainers && createdContainers.length > 0) {
      return (
        <Alert
          data-test-id="successAlert"
          onDismiss={() => setVisibleSuccess(false)}
          visible={visibleSuccess}
          dismissAriaLabel="Close alert"
          action={
            createdContainers.length === 1 && (
              <Link
                external
                externalIconAriaLabel="Opens in a new tab"
                href={`/assets-inventory/container/${createdContainers[0].containerInstance.containerId}`}
              >
                {t('View it here')}
              </Link>
            )
          }
          dismissible
          type="success"
        >
          {`${createdContainers.length} containers have been successfully created. `}
          <Button
            data-test-id="downloadButton"
            className="link-button"
            variant="link"
            onClick={downloadCsv}
          >
            {t('Download Created Containers')}
          </Button>
        </Alert>
      );
    }
    return <div />;
  };

  // Creates failure Alerts
  const renderFailureAlerts = () => {
    const failedContainers = createContainersResponse?.createdContainers?.filter(
      (c) => c.error || !c.containerInstance?.containerId
    );
    let errorMessage = '';
    if (failedContainers && failedContainers.length > 0) {
      setHasSubmitted(false);
      if (failedContainers.length === 1) {
        errorMessage = failedContainers[0].error;
      } else {
        errorMessage = `${failedContainers.length} containers failed to create.`;
      }
      return (
        <Alert
          data-test-id="failureAlert"
          onDismiss={() => setVisibleFailure(false)}
          visible={visibleFailure}
          dismissAriaLabel="Close alert"
          dismissible
          type="error"
        >
          {errorMessage}
        </Alert>
      );
    }
    return <div />;
  };

  if (creatingContainers) {
    return <Spinner size="big" />;
  }

  return (
    <div>
      {createContainerError && <Alert type="error">{createContainerError}</Alert>}

      {renderSuccessAlerts()}
      {renderFailureAlerts()}
    </div>
  );
};

export default CreateContainerAlerts;
